import { useState } from 'react';
// @mui
import { Box, Stack } from '@mui/material';
import {
  AssociatedMedicineDtoDosageIntervalEnum,
  GetMedicineDto,
} from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';
import {
  DosageAutofillPick,
  autofillPickFrom,
} from 'src/sections/@dashboard/profile/components/medication-form/dosageAutofill';
import {
  MedicineSearchOption,
  medicineOptionName,
} from 'src/sections/@dashboard/profile/components/medication-form/medicineSearchOptions';
import {
  MedicationStockFormValues,
  preselectedStockValues,
} from 'src/sections/@dashboard/profile/components/medication-form/medicationStockValues';
import { useWarehouseStockEnabled } from 'src/sections/@dashboard/warehouse/hooks/useClinicalStockProducts';
import AssociatedMedicineSearchField from './AssociatedMedicineSearchField';
import MedicationSummaryTable, { DOSAGE_LABELS } from './MedicationSummaryTable';
import {
  AssociatedMedicineValue,
  describeRowStock,
  formStockFromRow,
  rowStockFromForm,
} from './associatedMedicineStock';
import MedicationFormModal, {
  MedicationFormInitialValues,
  MedicationsFormModalValuesProps,
} from './medication-form/MedicationFormModal';

// ----------------------------------------------------------------------

export type { AssociatedMedicineValue } from './associatedMedicineStock';

interface AssociatedMedicinesEditorProps {
  value: AssociatedMedicineValue[];
  onChange: (next: AssociatedMedicineValue[]) => void;
  disabled?: boolean;
  /** Facility forms: each drug row may be taken from the Warehouse. */
  stockEnabled?: boolean;
  /** A catalogue template: its rows' stock is a default that names no recipient (F-7). */
  isTemplate?: boolean;
}

// ----------------------------------------------------------------------

/**
 * Controlled (non-RHF) editor for a list of associated medicines on a
 * treatment.  Props: `value` (current rows), `onChange` (immutable update),
 * `disabled`.
 *
 * Renders:
 *  - The shared medicine search ("Dodaj lek"); picking or typing one opens
 *    MedicationFormModal to add the row. On a Facility form with the
 *    Warehouse enabled the clinic's drugs lead, and a Warehouse pick opens
 *    the row with that Product in "Pobierz z magazynu" (#690)
 *  - MedicationSummaryTable showing all rows (click → edit modal)
 */
export default function AssociatedMedicinesEditor({
  value,
  onChange,
  disabled = false,
  stockEnabled = false,
  isTemplate = false,
}: AssociatedMedicinesEditorProps): React.ReactElement {
  const warehouseEnabled = useWarehouseStockEnabled();
  const warehouseAware = stockEnabled && warehouseEnabled;

  // ── Per-row product cache (productId → GetMedicineDto) ───────────────────
  // Populated when a user selects from the search dropdown this session.
  const [productCache, setProductCache] = useState<
    Record<string, GetMedicineDto>
  >({});

  // ── Modal state ──────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [pendingMedicine, setPendingMedicine] =
    useState<GetMedicineDto | null>(null);
  const [pendingMedicineName, setPendingMedicineName] = useState<string | null>(
    null,
  );
  /** The Product a Warehouse pick preselects for the new line (#690). */
  const [pendingStock, setPendingStock] = useState<
    MedicationStockFormValues | undefined
  >(undefined);
  /** #692 R1-2: the drug data of a "Dodaj lek" pick, which prefills the new line's dose. */
  const [pendingPick, setPendingPick] = useState<
    DosageAutofillPick | undefined
  >(undefined);

  // ── Row mutation helpers ─────────────────────────────────────────────────
  const removeRow = (index: number): void => {
    onChange(value.filter((_, i) => i !== index));
  };

  const clearPending = (): void => {
    setPendingMedicine(null);
    setPendingMedicineName(null);
    setPendingStock(undefined);
    setPendingPick(undefined);
  };

  const openAddModal = (): void => {
    setEditingIndex(null);
    setModalOpen(true);
  };

  const addTypedName = (name: string): void => {
    clearPending();
    setPendingMedicineName(name);
    openAddModal();
  };

  const addPicked = (option: MedicineSearchOption): void => {
    const { medicine } = option;
    if (medicine?.productId) {
      setProductCache((prev) => ({ ...prev, [medicine.productId]: medicine }));
    }
    setPendingMedicine(medicine);
    setPendingMedicineName(medicine ? null : medicineOptionName(option));
    setPendingStock(
      option.source === 'WAREHOUSE'
        ? preselectedStockValues(option.product.id)
        : undefined,
    );
    setPendingPick(autofillPickFrom(option));
    openAddModal();
  };

  // ── Modal submit handlers ────────────────────────────────────────────────
  /**
   * The form reports a dose left "do ustalenia" as `dosagePending` next to its
   * default dose of 0. A row keeps no dose at all then, which is what the table
   * and the patient's medication list read as pending.
   */
  const dosingFrom = (
    data: MedicationsFormModalValuesProps,
    fallbackInterval: AssociatedMedicineDtoDosageIntervalEnum | null,
  ): Pick<AssociatedMedicineValue, 'dosage' | 'dosageInterval'> =>
    data.dosagePending
      ? { dosage: null, dosageInterval: null }
      : {
          dosage: data.dosage ?? null,
          dosageInterval:
            (data.dosageInterval as AssociatedMedicineDtoDosageIntervalEnum) ??
            fallbackInterval,
        };

  const handleModalSubmit = (data: MedicationsFormModalValuesProps): void => {
    if (editingIndex === null) {
      // Add mode
      const newRow: AssociatedMedicineValue = {
        medicineProductId: data.medicineProductId,
        medicineName: data.name ?? pendingMedicine?.productName ?? '',
        ...dosingFrom(data, null),
        notes: data.notes ?? null,
        preparationType:
          data.preparationType ?? pendingMedicine?.preparationType ?? null,
        // No id: the server assigns one to a new line.
        ...(stockEnabled ? rowStockFromForm(data) : {}),
      };
      onChange([...value, newRow]);
      setModalOpen(false);
      clearPending();
    } else {
      // Edit mode — medicine identity (productId/name/preparationType) is
      // preserved from the existing row; only dosage/interval/notes and the
      // stock change. `existing.id` stays, so the line keeps its Stock Issue.
      // The modal's medicine step is read-only here (#692 §4), so the stock
      // is always of this same drug.
      const existing = value[editingIndex];
      if (!existing) return;
      const updatedRow: AssociatedMedicineValue = {
        ...existing,
        ...dosingFrom(data, existing.dosageInterval ?? null),
        notes: data.notes ?? null,
        ...(stockEnabled ? rowStockFromForm(data) : {}),
      };
      onChange(value.map((row, i) => (i === editingIndex ? updatedRow : row)));
      setModalOpen(false);
      setEditingIndex(null);
    }
  };

  const handleModalDelete = (): void => {
    if (editingIndex === null) return;
    removeRow(editingIndex);
    setModalOpen(false);
    setEditingIndex(null);
  };

  const handleModalClose = (): void => {
    setModalOpen(false);
    setEditingIndex(null);
    clearPending();
  };

  // ── Compute initialMedicine / initialValues for current modal mode ───────
  const editingRow = editingIndex !== null ? value[editingIndex] : null;

  const modalInitialMedicine: GetMedicineDto | null =
    editingIndex !== null
      ? productCache[editingRow?.medicineProductId ?? ''] ?? null
      : pendingMedicine;

  const modalInitialValues: MedicationFormInitialValues | undefined =
    editingIndex !== null && editingRow
      ? {
          ...(editingRow.dosage === null || editingRow.dosage === undefined
            ? { dosagePending: true }
            : { dosage: editingRow.dosage }),
          dosageInterval: (editingRow.dosageInterval ??
            'Day') as MedicationsFormModalValuesProps['dosageInterval'],
          notes: editingRow.notes ?? '',
        }
      : undefined;

  const modalInitialMedicineName: string | undefined =
    editingIndex !== null
      ? editingRow && !modalInitialMedicine
        ? editingRow.medicineName
        : undefined
      : pendingMedicineName ?? undefined;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Stack spacing={2}>
      {!disabled && (
        <AssociatedMedicineSearchField
          warehouseAware={warehouseAware}
          onPick={addPicked}
          onTypedName={addTypedName}
        />
      )}

      {/* Summary table — rows are clickable in edit mode */}
      <MedicationSummaryTable
        rows={value.map((m, i) => ({
          id: String(i),
          name: m.medicineName,
          // These rows still carry the old shape — a treatment's medicines move
          // to the structured model in #96.
          dosing:
            m.dosage === null || m.dosage === undefined
              ? translateKey('medication.table.dosagePending')
              : `${m.dosage} ${DOSAGE_LABELS[m.dosageInterval ?? 'Day'] ?? ''}`.trim(),
          notes: m.notes,
          stock: describeRowStock(m),
        }))}
        canEdit={!disabled}
        emptyMessage={translateKey('hardcoded.brak_przypisanych_lekow')}
        onRowClick={(index) => {
          setEditingIndex(index);
          clearPending();
          setModalOpen(true);
        }}
      />

      {/* Add / Edit modal.
          The modal renders its own <form>; this editor lives inside the parent
          treatment form. React portals bubble events through the React tree, so
          without this guard the modal's submit would bubble up and submit the
          parent treatment form (closing it). Contain it here. */}
      <Box onSubmit={(e) => e.stopPropagation()}>
        <MedicationFormModal
          isModalOpen={modalOpen}
          closeModal={handleModalClose}
          onSubmit={handleModalSubmit}
          onDelete={editingIndex !== null ? handleModalDelete : undefined}
          hideReminder
          hideLifecycle
          showDeleteButton={editingIndex !== null}
          medicineLocked={editingIndex !== null}
          initialMedicine={modalInitialMedicine}
          initialMedicineProductId={editingRow?.medicineProductId ?? undefined}
          initialValues={modalInitialValues}
          initialMedicineName={modalInitialMedicineName}
          canEdit={!disabled}
          stockEnabled={stockEnabled}
          stockVariant={isTemplate ? 'TEMPLATE_LINE' : 'TREATMENT_LINE'}
          initialStock={
            editingRow ? formStockFromRow(editingRow, isTemplate) : pendingStock
          }
          newMedicinePick={pendingPick}
        />
      </Box>
    </Stack>
  );
}
